import { useNavigate, useParams, generatePath } from 'react-router'
import { useEffect, useState } from 'react'
import { Tabs, Tab, Typography, Breadcrumbs, Link, Box, CircularProgress } from '@mui/material'
import { TabDisplay } from 'Components/TabDisplay'
import { EnrollmentDetailsPage } from 'Pages/Enrollments/EnrollmentDetailsPage'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { SupplementalNutritionList } from 'Pages/CareNetworkManagement/CareNetworkDetail/SupplementalNutritionList'
import { useRefresh } from 'ContextProviders/RefreshProvider'
import { sendGet } from 'hooks/use-fetch'
import { ActionMenu } from 'Components/Menu/ActionMenu'
import { ActionItem } from 'Components/Menu/ActionItem'
import { NewEnrollmentNotes } from 'Pages/ServiceProvider/Enrollments/NewEnrollmentNotes'
import { ENROLLMENT_MANAGEMENT_DETAILS, ENROLLMENT_MANAGEMENT } from 'Pages/Routes'
import { type BasicEnrollmentData } from 'core/types'
import { usePermissions } from 'hooks/use-permissions'
import { AddReminderModal } from 'Components/Notifications/AddReminderModal'
import { ChildAndGuardianDocumentList } from 'Components/DocumentList/ChildAndGuardianDocumentList'

export function EnrollmentPage (): JSX.Element {
  const { id } = useParams()
  const refreshTime = useRefresh()
  const perms = usePermissions()
  const [showMenu, setShowMenu] = useState(false)
  const [enrollmentData, setEnrollmentData] = useState<BasicEnrollmentData | null>(null)
  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response } = await sendGet(`/NewEnrollment/GetBasicDetails?id=${id ?? 0}`, {})
      setEnrollmentData(response)
    }
    if (id != null) { void loadData() }
  }, [id, refreshTime])
  const nav = useNavigate()
  const goHome = (): void => {
    nav('/')
  }
  const goBack = (): void => {
    nav(-1)
  }
  const goToEnrollmentServicePage = (): void => {
    const enrollmentListPage = generatePath(ENROLLMENT_MANAGEMENT_DETAILS, { serviceId: enrollmentData?.serviceID?.toString() ?? '' })
    nav(enrollmentListPage)
  }
  const goToEnrollmentSplash = (): void => {
    const enrollmentSplash = generatePath(ENROLLMENT_MANAGEMENT, {})
    nav(enrollmentSplash)
  }
  const [activeTab, setActiveTab] = useState(0)
  const printDetails = (): void => {
    window.open(enrollmentData?.reportURL, '_blank')?.focus()
    setShowMenu(false)
  }
  const [showReminder, setShowReminder] = useState(false)
  const displayReminder = (): void => {
    setShowReminder(true)
    setShowMenu(false)
  }

  if (enrollmentData == null) { return <CircularProgress /> }
  return <>
    <AddReminderModal
      childId={enrollmentData.childID}
      show={showReminder}
      setShow={setShowReminder}
    />
    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
      <Typography variant='h3' fontWeight={'bold'}>{enrollmentData.childName} {enrollmentData.serviceName} Enrollment Details</Typography>
      <ActionMenu open={showMenu} onClose={() => { setShowMenu(false) }} onShow={() => { setShowMenu(true) }} testId='Enrollment Actions' menuProps={{ border: 'none' }}>
        <Box>
          <ActionItem testId='printDetails' onClick={printDetails} >Print Details</ActionItem>
          {perms.Enrollment_Communication_Edit && <ActionItem testId='setReminder' onClick={displayReminder} >Set Reminder</ActionItem>}
        </Box>
      </ActionMenu>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
      <ArrowBackIcon onClick={goBack} sx={{ cursor: 'pointer' }} />
      <Breadcrumbs >
        <Link onClick={goHome} sx={{ textTransform: 'none', color: 'inherit', textDecorationLine: 'none', cursor: 'pointer' }}>Home</Link>
        <Link onClick={goToEnrollmentSplash} sx={{ textTransform: 'none', color: 'inherit', textDecorationLine: 'none', cursor: 'pointer' }}>Enrollments</Link>
        <Link onClick={goToEnrollmentServicePage} sx={{ textTransform: 'none', color: 'inherit', textDecorationLine: 'none', cursor: 'pointer' }}>{enrollmentData.serviceName} Enrollments</Link>
      </Breadcrumbs>
    </Box>
    <Tabs value={activeTab}
      data-testid='tabs'
      onChange={(e, val) => { setActiveTab(val) }}
      aria-label="ISD Settings Tabs"
      textColor="secondary"
      indicatorColor="secondary"
      variant="standard"
      scrollButtons="auto"
    >
      <Tab
        label="Details" value={0} data-testid="details"
      />
      {perms.Enrollment_Communication && <Tab label="Communication" value={1} data-testid="communication" /> }
      {perms.Enrollment_Documentation && <Tab label="Documents" value={2} data-testid="documents" />}
      {(perms.CareNetwork_SupplementalNutrition || perms.CareNetwork_SensitiveDetails) && <Tab label="Supplemental Nutrition" value={3} data-testid="nutrition" />}
    </Tabs>
    <TabDisplay
      index={0}
      value={activeTab}
    >
      <EnrollmentDetailsPage />
    </TabDisplay>
    <TabDisplay
      index={1}
      value={activeTab}
    >
      <NewEnrollmentNotes childID={enrollmentData?.childID} enrollmentID={enrollmentData?.id} isNotesPermission={true} isEditable={perms.Enrollment_Communication_Edit} />
    </TabDisplay>

    <TabDisplay
      index={2}
      value={activeTab}
    >
      <ChildAndGuardianDocumentList
        childID={enrollmentData.childID}
        editable={perms.Enrollment_Documentation_Edit}
        isdID={enrollmentData.isdID}
      />
    </TabDisplay >
    <TabDisplay
      index={3}
      value={activeTab}
    >
      <SupplementalNutritionList childId={enrollmentData?.childID} />
    </TabDisplay>
  </>
}
